import { flow, types, getEnv, Instance } from "mobx-state-tree";
import { when } from "mobx";
import FilePonyfill from "@tanker/file-ponyfill";

import { api } from "../api/ApiService";
import { endpoints } from "../api/ApiEndpoints";
import { UploadFile, ParserResult, MediaFile, UploadStandaloneFile } from "./UploadFilesModels";
import { FileExtended, FileUploadTrackerWrapper, FileUploadTracker } from "./FileUploadTracker";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { PubSubEvents } from "../PubSubEvents";
import {
  InspectionFileDataUpdateOption,
  InspectionRelevanceUpdateOption,
  InspectionRelevanceIntervalOption,
} from "../../generated/Inspections";

export enum UploadStatusOption {
  undefined = 0,
  failed = 1,
  ongoing = 2,
  completed = 3,
  canceled = 4,
}

const UploadStatusModel = types
  .model({
    completed: 0,
    completedWithWarnings: 0,
    failed: 0,
  })
  .actions((self) => ({
    setToInitialState() {
      self.completed = 0;
      self.completedWithWarnings = 0;
      self.failed = 0;
    },
    setUploadStatusOverview(filesUploadStatus: FileUploadTrackerWrapper[]) {
      const status = filesUploadStatus.reduce(
        (results: any, file) => {
          if (file.wrapper.isCompleted)
            return {
              completed: results.completed + 1,
              failed: results.failed,
              completedWithWarnings: results.completedWithWarnings,
            };
          if (file.wrapper.isCompletedWithWarnings)
            return {
              completed: results.completed,
              failed: results.failed,
              completedWithWarnings: results.completedWithWarnings + 1,
            };
          if (file.wrapper.isFailed)
            return {
              completed: results.completed,
              failed: results.failed + 1,
              completedWithWarnings: results.completedWithWarnings,
            };
          if (file.wrapper.isExcluded)
            return {
              completed: results.completed,
              failed: results.failed + 1,
              completedWithWarnings: results.completedWithWarnings,
            };
          if (file.wrapper.isMissing)
            return {
              completed: results.completed,
              failed: results.failed + 1,
              completedWithWarnings: results.completedWithWarnings,
            };
          return results;
        },
        { completed: 0, failed: 0, completedWithWarnings: 0 }
      );

      self.completed = status.completed;
      self.completedWithWarnings = status.completedWithWarnings;
      self.failed = status.failed;
    },
  }));

const UploadResultModel = types
  .model("UploadResultModel", {
    status: UploadStatusOption.undefined,
    errorKey: "",
    progress: 0,
    inspectionFileToUploadCount: types.maybe(types.number),
    uploadStatus: UploadStatusModel,
  })
  .views((self) => ({
    get uploadFinished() {
      return (
        !!self.status &&
        self.status !== UploadStatusOption.ongoing &&
        self.inspectionFileToUploadCount === 0
      );
    },
    get uploadOngoing() {
      return !!self.status && self.status === UploadStatusOption.ongoing;
    },
  }))
  .actions((self) => ({
    inspectionFileUploaded() {
      self.inspectionFileToUploadCount! -= 1;
    },
    markUploadAsFailed(errorKey: string = "general.upload.server-failed") {
      self.status = UploadStatusOption.failed;
      self.errorKey = errorKey;
    },
    markUploadAsCompleted() {
      self.status = UploadStatusOption.completed;
      self.progress = 100;
    },
    markUploadAsCanceled() {
      self.status = UploadStatusOption.canceled;
    },
    markUploadAsOngoing(progress: number) {
      self.status = UploadStatusOption.ongoing;
      self.progress = progress;
    },
    setToInitialState() {
      self.status = UploadStatusOption.undefined;
      self.errorKey = "";
      self.progress = 0;
      self.uploadStatus.setToInitialState();
    },
  }));

export const UploadFilesServiceModel = types
  .model("UploadFilesServiceModel", {
    singleFile: types.optional(types.boolean, false),
    filesForUpload: types.optional(types.array(types.frozen<FileExtended>()), []),
    filesUploadStatus: types.optional(types.array(types.frozen<FileUploadTrackerWrapper>()), []),
    dropzoneIsVisible: false,
    isRemainingStorageLoading: false,
    uploadProgressModalIsVisbile: false,
    uploadSnackbarIsVisible: false,
    uploadSummaryModalIsVisible: false,
    uploadProgress: UploadResultModel,
    fileDataUpdateOption: InspectionFileDataUpdateOption.NewAndExistingObjects,
    noInspectionFileUploaded: false,
    remainingStorageSize: 0,
    canStartUpload: false,
    projectId: types.maybeNull(types.number),
    filesStatusRenderTrigger: false,
    inspectionsRelevanceUpdateOption: InspectionRelevanceUpdateOption.MarkOutdatedOlderThanInterval,
    intervalOption: InspectionRelevanceIntervalOption.Days7,
  })
  .views((self) => ({
    get uploadStatus(): UploadStatusOption {
      return self.uploadProgress.status;
    },
    get totalSizeOfFiles(): number {
      return self.filesForUpload.map((f) => f.size).reduce((a, b) => a + b);
    },
    get noFilesToUpload(): boolean {
      return !self.filesForUpload || !self.filesForUpload.length;
    },
  }))
  .views((self) => ({
    get organizationStorageIsFull() {
      return 2 * self.totalSizeOfFiles > self.remainingStorageSize;
    },
  }))
  .actions((self) => {
    const setOverallProgres = () => {
      const completedUploads = self.filesUploadStatus.filter(
        (file) => file.wrapper.isFinished
      ).length;
      const progress = (completedUploads / self.filesUploadStatus.length) * 100;

      if (progress === 100) self.uploadProgress.markUploadAsCompleted();
      else self.uploadProgress.markUploadAsOngoing(progress);

      self.filesStatusRenderTrigger = !self.filesStatusRenderTrigger;
    };

    return {
      fetchStorageRelatedData: flow(function* () {
        try {
          self.isRemainingStorageLoading = true;
          const endpoint = endpoints.get("remaining_storage");
          const remainingStorage = yield getEnv(self).api.get(endpoint);
          self.isRemainingStorageLoading = false;
          self.remainingStorageSize = remainingStorage;
        } catch (err) {
          self.uploadProgress.markUploadAsFailed();
        }
      }),
      uploadOverallProgressWasMade() {
        self.canStartUpload = false;
        setOverallProgres();
        self.uploadProgress.uploadStatus.setUploadStatusOverview(self.filesUploadStatus);
      },
      handleDataUpdateOptionChanged(updateOption: InspectionFileDataUpdateOption) {
        self.fileDataUpdateOption = updateOption;
      },
      setDefaultDataUpdateOption() {
        self.fileDataUpdateOption = InspectionFileDataUpdateOption.NewAndExistingObjects;
      },
      handleInspectionsRelevanceUpdateOptionChanged(updateOption: InspectionRelevanceUpdateOption) {
        self.inspectionsRelevanceUpdateOption = updateOption;
      },
      setDefaultInspectionsRelevanceOption() {
        self.inspectionsRelevanceUpdateOption =
          InspectionRelevanceUpdateOption.MarkOutdatedOlderThanInterval;
      },
      handleInspectionsRelevanceIntervalChanged(updateOption: InspectionRelevanceIntervalOption) {
        self.intervalOption = updateOption;
      },
      setDefaultInspectionsRelevanceIntervalOption() {
        self.intervalOption = InspectionRelevanceIntervalOption.Days7;
      },
      showUploadSummaryModal() {
        self.uploadSummaryModalIsVisible = true;
      },
      hideUploadSummaryModal() {
        self.uploadSummaryModalIsVisible = false;
      },
      showUploadProgressModal() {
        self.uploadProgressModalIsVisbile = true;
      },
      hideUploadProgressModal() {
        self.uploadProgressModalIsVisbile = false;
      },
      showUploadSnackbar() {
        self.uploadSnackbarIsVisible = true;
      },
      hideUploadSnackbar() {
        self.uploadSnackbarIsVisible = false;
      },
    };
  })
  .actions((self) => ({
    showUploadModal() {
      self.dropzoneIsVisible = true;
      self.fetchStorageRelatedData();
    },
    hideUploadModal() {
      self.dropzoneIsVisible = false;
    },
    addFileAsNotFound(mediaFileId: number, fileName: string) {
      const file = new FilePonyfill([], fileName, { type: "text/plain" });
      const extendedFile = new FileExtended(file, "");
      const fileUploadStatus = new FileUploadTrackerWrapper(
        extendedFile,
        self.uploadOverallProgressWasMade,
        mediaFileId
      );
      fileUploadStatus.wrapper.notFound("general.notFound");
      self.filesUploadStatus.push(fileUploadStatus);
    },
    markNotRelevantFilesAsExcluded() {
      self.filesUploadStatus.forEach((fileUpload) => {
        if (fileUpload.wrapper.status === undefined) {
          fileUpload.wrapper.excluded("general.excluded");
        }
      });
    },
    finalizeUpload() {
      if (!self.uploadProgressModalIsVisbile) {
        self.showUploadSummaryModal();
        self.hideUploadSnackbar();
      }
      self.uploadProgress.markUploadAsCompleted();
      self.setDefaultDataUpdateOption();
      self.setDefaultInspectionsRelevanceOption();
      self.setDefaultInspectionsRelevanceIntervalOption();
      self.filesForUpload.clear();
    },
  }))
  .actions((self) => {
    const getMediaFileToUpload = (currentMediaFileInfo: MediaFile) => {
      const getMediaFilesMatchedByName = (currentMediaFileInfo: MediaFile) => {
        return self.filesUploadStatus
          .filter((f) => f.wrapper.name.toLowerCase() === currentMediaFileInfo.name.toLowerCase())
          .map((f) => f.wrapper);
      };
      const oneMediaFileIsFound = (mediaFilesMatchedByName: FileUploadTracker[]) => {
        return mediaFilesMatchedByName.length === 1;
      };
      const mediaFilePathIsNotSpecified = (currentMediaFileInfo: MediaFile): boolean => {
        return !currentMediaFileInfo.importPath;
      };
      const getMediaFileByName = (
        currentMediaFileInfo: MediaFile
      ): FileUploadTracker | undefined => {
        return self.filesUploadStatus.find(
          (f) => f.wrapper.name.toLowerCase() === currentMediaFileInfo.name.toLowerCase()
        )?.wrapper;
      };
      const getMediaFilePath = (currentMediaFileInfo: MediaFile) => {
        const currentMediaFilePath = currentMediaFileInfo.importPath.toLowerCase();
        currentMediaFilePath.replace("\\", "/");
        const fullPath = `/${currentMediaFilePath}/${currentMediaFileInfo.name}`;
        return fullPath;
      };
      const getMediaFileByExactPath = (
        mediaFileToUpload: FileUploadTracker | undefined,
        path: string
      ) => {
        mediaFileToUpload = self.filesUploadStatus.find(
          (f) => f.wrapper.fileExtended.lowerCasePath === path
        )?.wrapper;
        return mediaFileToUpload;
      };
      const getFileMatchingPartialPath = (
        mediaFilePath: string,
        mediaFileName: string
      ): FileUploadTracker | undefined => {
        const files: Array<FileUploadTrackerWrapper> = self.filesUploadStatus;
        let copyMediaFilePath = mediaFilePath;
        const splittedMediaFilePath = mediaFilePath.split("/");
        const lastPartialPathIndex = splittedMediaFilePath.length - 1;
        for (let i = 0; i < lastPartialPathIndex; i++) {
          const folderName = splittedMediaFilePath[i];
          const extractedPartialMediaPath = copyMediaFilePath.substring(
            folderName.length + 1,
            copyMediaFilePath.length
          );
          copyMediaFilePath = extractedPartialMediaPath;
          const file = files.find(
            (f) =>
              f.wrapper.fileExtended.lowerCasePath.endsWith(extractedPartialMediaPath) &&
              f.wrapper.name === mediaFileName
          );
          if (file) {
            return file.wrapper;
          }
        }
        return undefined;
      };

      let mediaFileToUpload: FileUploadTracker | undefined;
      const mediaFilesMatchedByName = getMediaFilesMatchedByName(currentMediaFileInfo);

      if (
        oneMediaFileIsFound(mediaFilesMatchedByName) ||
        mediaFilePathIsNotSpecified(currentMediaFileInfo)
      ) {
        return getMediaFileByName(currentMediaFileInfo);
      }

      const path = getMediaFilePath(currentMediaFileInfo);
      mediaFileToUpload = getMediaFileByExactPath(mediaFileToUpload, path);

      if (mediaFileToUpload) {
        return mediaFileToUpload;
      }

      return getFileMatchingPartialPath(path, currentMediaFileInfo.name);
    };

    const uploadStandaloneFile = async (
      file: FileUploadTracker,
      onUploadFinished: (file: FileUploadTracker) => void
    ) => {
      file.ongoing();

      const onFileUploadProgress = (progressEvent: ProgressEvent) => {
        file.updateProgress(progressEvent);
      };

      const endpoint = endpoints.get("mediafiles");
      try {
        const response: UploadStandaloneFile = await getEnv(self).api.upload(
          endpoint,
          file.getFileFormData(),
          onFileUploadProgress
        );
        file.id = response.data;
        onUploadFinished(file);
      } catch (error) {
        file.failed("general.failed");
        self.uploadProgress.inspectionFileUploaded();
        const applicationError = error as ApplicationError;
        errorHandler.handleErrorInSnackbar(applicationError);
      }
    };

    const uploadInspectionFile = async (file: FileUploadTracker) => {
      file.ongoing();

      const onFileUploadProgress = (progressEvent: ProgressEvent) => {
        file.updateProgress(progressEvent);
      };

      const endpoint = endpoints.get("upload_files_in_project_async");
      endpoint.routeParams = [self.projectId!];
      endpoint.queryParams = `inspectionFileDataUpdateOption=${self.fileDataUpdateOption}&relevanceOption=${self.inspectionsRelevanceUpdateOption}&intervalOption=${self.intervalOption}`;
      try {
        const response: UploadFile = await getEnv(self).api.upload(
          endpoint,
          file.getFileFormData(),
          onFileUploadProgress
        );

        if (response.data.inspectionFileNormError) {
          file.setNormError(response.data.inspectionFileNormError);
          file.failed("general.failed");
          self.uploadProgress.inspectionFileUploaded();
          return undefined;
        }
      } catch (error) {
        file.failed("general.failed");
        self.uploadProgress.inspectionFileUploaded();
        const applicationError = error as ApplicationError;
        errorHandler.handleErrorInSnackbar(applicationError);
      }
    };

    const subscribeInspectionFileUploadFinished = () => {
      const parseErrorMessage = (errorMessage: string) => {
        const stringWithoutSingleQuotes = errorMessage.replace(/'/g, "");
        return JSON.parse(stringWithoutSingleQuotes);
      };
      const mapFailedResponse = (response: any): ParserResult[] => {
        let mappedResponse: ParserResult[] = [];

        mappedResponse.push({
          severity: response["Severity"],
          category: response["Category"],
          text: response["Text"],
          rowNumber: response["RowNumber"],
          name: response["Name"],
          number: response["Number"],
          columnName: response["ColumnName"],
        });

        return mappedResponse;
      };
      const uploadRelatedMediaFiles = async (inspectionFileId: number) => {
        try {
          const endpoint = endpoints.get("project_media_files");
          endpoint.routeParams = [self.projectId!, inspectionFileId];

          const mediaFiles = await getEnv(self).api.get(endpoint);
          await uploadMediaFiles(mediaFiles);
          self.uploadProgress.inspectionFileUploaded();
        } catch (error) {
          const applicationError = error as ApplicationError;
          errorHandler.handleErrorInSnackbar(applicationError);
        }
      };

      const subscribeToken = PubSub.subscribe(
        PubSubEvents.InspectionFileUploadFinished,
        (event: string, notification: Notification) => {
          console.log("data", notification.data);
          if (notification.data) {
            const inspectionFileUploaded = self.filesUploadStatus.find(
              (x) => x.wrapper.name === notification.data.inspectionFileName
            );
            if (notification.data.error) {
              inspectionFileUploaded?.wrapper.failed("general.failed");
              const parsedErrorMessage = parseErrorMessage(notification.data.error);
              const mappedError = mapFailedResponse(parsedErrorMessage);
              inspectionFileUploaded!.wrapper.parsingErrorsDetails = mappedError;
              self.uploadProgress.inspectionFileUploaded();
              return;
            }
            if (notification.data.inspectionFileId) {
              if (notification.data.inspectionFileNotifications.length > 0) {
                inspectionFileUploaded?.wrapper.completedWithWarnings(
                  notification.data.inspectionFileNotifications,
                  "general.upload.success-with-warnings"
                );
              } else {
                inspectionFileUploaded?.wrapper.completed();
              }
              uploadRelatedMediaFiles(notification.data.inspectionFileId);
            }
          }
        }
      );

      when(
        () => self.uploadProgress.inspectionFileToUploadCount === 0,
        self.markNotRelevantFilesAsExcluded
      );

      when(
        () => self.uploadProgress.uploadFinished,
        () => {
          self.finalizeUpload();
          PubSub.unsubscribe(subscribeToken);
        }
      );
    };

    const startUploadStandaloneFilesAsync = (
      onUploadFinished: (file: FileUploadTracker) => void
    ) => {
      try {
        const videosToUpload = self.filesUploadStatus.filter(
          (f) => f.wrapper.fileExtended.isVideoFileType
        );
        self.uploadProgress.inspectionFileToUploadCount = videosToUpload.length;

        videosToUpload.forEach(
          async (fileUploadStatus) =>
            await uploadStandaloneFile(fileUploadStatus.wrapper, onUploadFinished)
        );
      } catch (err) {
        self.uploadProgress.markUploadAsFailed();
      }
    };

    const startUploadInspectionFilesAsync = () => {
      try {
        const inspectionFilesToUpload = self.filesUploadStatus.filter(
          (f) => f.wrapper.fileExtended.isAcceptedInspectionFileType
        );
        self.uploadProgress.inspectionFileToUploadCount = inspectionFilesToUpload.length;

        inspectionFilesToUpload.forEach(
          async (fileUploadStatus) => await uploadInspectionFile(fileUploadStatus.wrapper)
        );

        subscribeInspectionFileUploadFinished();
      } catch (err) {
        self.uploadProgress.markUploadAsFailed();
      }
    };

    const startUploadMediaFilesAsync = (inspectionId: number) => {
      self.uploadProgress.inspectionFileToUploadCount = 0;

      const newMediaFiles = self.filesUploadStatus;

      newMediaFiles.forEach(async (f) => {
        f.wrapper.ongoing();
        try {
          const endpoint = endpoints.get("upload_inspection_media_files");
          endpoint.routeParams = [inspectionId];

          await getEnv(self).api.upload(endpoint, f.wrapper.getFileFormData(), (e: ProgressEvent) =>
            f.wrapper?.updateProgress(e)
          );

          f.wrapper.completed();
        } catch (err) {
          f.wrapper.failed("general.failed");
        }
      });

      when(() => self.uploadProgress.uploadFinished, self.finalizeUpload);
    };

    const uploadMediaFile = async (
      mediaFileToUpload: FileUploadTracker,
      projectId: number,
      mediaFileId: number
    ) => {
      mediaFileToUpload.ongoing();

      const onMediaFileUploadProgress = (progressEvent: ProgressEvent) => {
        mediaFileToUpload?.updateProgress(progressEvent);
      };

      try {
        const endpoint = endpoints.get("project_media_file");
        endpoint.routeParams = [projectId, mediaFileId];

        await getEnv(self).api.upload(
          endpoint,
          mediaFileToUpload.getFileFormData(),
          onMediaFileUploadProgress
        );

        mediaFileToUpload.completed();
      } catch (err) {
        mediaFileToUpload.failed("general.failed");
      }
    };

    const uploadMediaFiles = async (mediaFilesInfo: Array<MediaFile>) => {
      for (let index = 0; index < mediaFilesInfo.length; index++) {
        const currentMediaFileInfo = mediaFilesInfo[index];

        const mediaFileToUpload = getMediaFileToUpload(currentMediaFileInfo);

        if (!mediaFileToUpload) {
          self.addFileAsNotFound(currentMediaFileInfo.id, currentMediaFileInfo.name);
          continue;
        }
        mediaFileToUpload.id = currentMediaFileInfo.id;

        if (!mediaFileToUpload.hasContent()) continue;

        if (currentMediaFileInfo.isUploadedInStorage && !currentMediaFileInfo.isDeleted) {
          mediaFileToUpload.completed();
          continue;
        }

        uploadMediaFile(mediaFileToUpload, self.projectId!, currentMediaFileInfo.id);
      }
    };

    return {
      showUploadProgress() {
        self.showUploadProgressModal();
        self.hideUploadSnackbar();
        self.hideUploadSummaryModal();
      },
      hideUploadProgress() {
        self.hideUploadProgressModal();
        if (self.uploadProgress.uploadOngoing) {
          self.showUploadSnackbar();
        } else {
          self.hideUploadSnackbar();
          self.hideUploadSummaryModal();
        }
      },
      startStandaloneUpload(
        onUploadFinished: (file: FileUploadTracker) => void
      ) {
        self.showUploadProgressModal();
        self.hideUploadSnackbar();

        if (self.noFilesToUpload) {
          self.uploadProgress.markUploadAsFailed("general.upload.no-files");
          return;
        }

        self.uploadProgress.setToInitialState();
        self.filesUploadStatus.clear();
        self.filesUploadStatus.push(
          ...self.filesForUpload.map(
            (f) => new FileUploadTrackerWrapper(f, self.uploadOverallProgressWasMade)
          )
        );

        self.canStartUpload = true;

        startUploadStandaloneFilesAsync(onUploadFinished);
      },
      startProjectUpload(projectId: number) {
        const inspectionFilesForUpload = self.filesForUpload.filter(
          (file) => file.isAcceptedInspectionFileType
        );

        if (!inspectionFilesForUpload.length) {
          self.noInspectionFileUploaded = true;
          return;
        }

        self.showUploadProgressModal();
        self.hideUploadModal();
        self.hideUploadSummaryModal();
        self.hideUploadSnackbar();

        if (self.organizationStorageIsFull) {
          self.hideUploadProgressModal();
          self.uploadProgress.markUploadAsFailed("general.upload.no-storage");
          snackbarNotificationsService.showError("general.upload.no-storage");
          self.filesForUpload.clear();
          return;
        }

        if (self.noFilesToUpload) {
          self.uploadProgress.markUploadAsFailed("general.upload.no-files");
          return;
        }

        self.uploadProgress.setToInitialState();
        self.filesUploadStatus.clear();
        self.filesUploadStatus.push(
          ...self.filesForUpload.map(
            (f) => new FileUploadTrackerWrapper(f, self.uploadOverallProgressWasMade)
          )
        );

        self.canStartUpload = true;
        self.projectId = projectId;

        startUploadInspectionFilesAsync();
      },
      startInspectionUpload(inspectionId: number) {
        self.showUploadProgressModal();
        self.hideUploadSummaryModal();
        self.hideUploadSnackbar();

        if (self.organizationStorageIsFull) {
          self.hideUploadProgressModal();
          self.uploadProgress.markUploadAsFailed("general.upload.no-storage");
          snackbarNotificationsService.showError("general.upload.no-storage");
          self.filesForUpload.clear();
          return;
        }

        if (self.noFilesToUpload) {
          self.uploadProgress.markUploadAsFailed("general.upload.no-files");
          return;
        }

        self.uploadProgress.setToInitialState();
        self.filesUploadStatus.clear();
        self.filesUploadStatus.push(
          ...self.filesForUpload.map(
            (f) => new FileUploadTrackerWrapper(f, self.uploadOverallProgressWasMade)
          )
        );

        self.canStartUpload = true;

        startUploadMediaFilesAsync(inspectionId);
      },
      cancelUpload() {
        self.filesForUpload.clear();
        self.noInspectionFileUploaded = false;
        self.uploadProgress.markUploadAsCanceled();
        self.hideUploadSnackbar();
      },
      addFilesForUpload(droppedFiles: Array<FileExtended>) {
        self.noInspectionFileUploaded = false;
        if (self.singleFile) {
          if (droppedFiles.length === 0) return;
          self.filesForUpload.clear();
          self.filesForUpload.push(droppedFiles[0]);
        }
        else {
        self.filesForUpload.push(
          ...droppedFiles.filter(
            (newFile) =>
              !self.filesForUpload.some(
                (oldFile) =>
                  oldFile.file.name === newFile.file.name && oldFile.path === newFile.path
              )
          )
        );}
      },
      removeFile(fileName: string) {
        const file = self.filesForUpload.find(
          (fileExtended) => fileExtended.file.name === fileName
        );
        self.filesForUpload.remove(file!);
        if (!self.filesForUpload.length) self.noInspectionFileUploaded = false;
      },
      allowOnlySingleFile(value: boolean) {
        self.singleFile = true;
      },
      clear() {
        self.filesForUpload.clear();
        self.filesUploadStatus.clear();
        self.uploadProgressModalIsVisbile = false;
      },
      async reuploadMediaFile(
        mediaFileToUpload: FileUploadTracker,
        projectId: number,
        mediaFileId: number
      ) {
        await uploadMediaFile(mediaFileToUpload, projectId, mediaFileId);
        if (self.uploadProgress.uploadFinished) self.finalizeUpload();
      },
    };
  });

export const uploadFilesService = UploadFilesServiceModel.create(
  { uploadProgress: { uploadStatus: {} } },
  { api }
);

export type UploadFilesService = Instance<typeof UploadFilesServiceModel>;
